import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Save, Trash2, Plus, ClipboardCheck, AlertTriangle, BarChart3, User, Calendar } from 'lucide-react';

export default function TaskAssignment() {
  const [employees, setEmployees] = useState([]);
  const [masterTasks, setMasterTasks] = useState([]);
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchEmployees();
    fetchMasterTasks();
  }, []);

  useEffect(() => {
    if (selectedEmpId) { fetchAssignments(); setAnalysis(null); }
  }, [selectedEmpId]);

  const fetchEmployees = async () => {
    try { const r = await api.get('/employees'); setEmployees(r.data.filter(e => e.is_active !== false)); }
    catch { toast.error('Failed to fetch employees'); }
  };

  const fetchMasterTasks = async () => {
    try { const r = await api.get('/task-master'); setMasterTasks(r.data.filter(t => t.is_active)); }
    catch { toast.error('Failed to fetch tasks'); }
  };

  const fetchAssignments = async () => {
    try {
      const r = await api.get(`/task-assignments/${selectedEmpId}`);
      setAssignments(r.data.tasks || []);
    } catch { setAssignments([]); }
  };

  const addTask = () => {
    const unassigned = masterTasks.filter(mt => !assignments.some(a => a.task_id === mt.id));
    if (unassigned.length === 0) { toast.info('All tasks already assigned'); return; }
    setAssignments(prev => [...prev, { task_id: unassigned[0].id, weightage: 0 }]);
  };

  const removeTask = (idx) => {
    setAssignments(prev => prev.filter((_, i) => i !== idx));
  };

  const updateTask = (idx, field, value) => {
    setAssignments(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));
  };

  const totalWeightage = assignments.reduce((s, a) => s + (parseFloat(a.weightage) || 0), 0);

  const handleSave = async () => {
    if (assignments.length === 0) { toast.error('Add at least one task'); return; }
    if (Math.abs(totalWeightage - 100) > 0.01) { toast.error(`Total weightage must be 100%. Current: ${totalWeightage}%`); return; }
    setSaving(true);
    try {
      await api.post(`/task-assignments/${selectedEmpId}`, { tasks: assignments });
      toast.success('Tasks assigned successfully');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const fetchAnalysis = async () => {
    if (!selectedEmpId) return;
    try {
      let url = `/employee-tasks/analysis/${selectedEmpId}`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length) url += '?' + params.join('&');
      const r = await api.get(url);
      setAnalysis(r.data);
    } catch { toast.error('Failed to fetch analysis'); }
  };

  const selectedEmp = employees.find(e => e.id === selectedEmpId);
  const taskMap = Object.fromEntries(masterTasks.map(t => [t.id, t]));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="task-assignment-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800">Task Assignment</h1>
        <p className="text-slate-500">Assign daily tasks to employees with weightage (must total 100%)</p>
      </div>

      {/* Employee Selector */}
      <Card className="bg-white border shadow-sm">
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
            <div className="flex-1 space-y-2">
              <label className="text-sm font-medium text-slate-700">Select Employee</label>
              <Select value={selectedEmpId} onValueChange={setSelectedEmpId}>
                <SelectTrigger data-testid="employee-select">
                  <SelectValue placeholder="Choose an employee..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>
                      <span className="flex items-center gap-2">
                        <User className="w-3.5 h-3.5 text-slate-400" />
                        {e.name} <span className="text-slate-400 ml-1">#{e.employee_code}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedEmpId && (
              <Button onClick={addTask} variant="outline" className="border-[#FF9933] text-[#FF9933]" data-testid="add-assignment-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Task
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Assignment Table */}
      {selectedEmpId && (
        <Card className="bg-white border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-[#FF9933]" />
              Tasks for {selectedEmp?.name}
              <Badge className={Math.abs(totalWeightage - 100) < 0.01 ? 'bg-green-100 text-green-700 ml-auto' : 'bg-red-100 text-red-600 ml-auto'}>
                Total: {totalWeightage}%
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table data-testid="assignment-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold w-12">#</TableHead>
                  <TableHead className="font-semibold">Task</TableHead>
                  <TableHead className="font-semibold w-32">Weightage (%)</TableHead>
                  <TableHead className="font-semibold text-right w-20">Remove</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignments.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-500">No tasks assigned. Click "Add Task" to begin.</TableCell></TableRow>
                ) : assignments.map((a, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="text-slate-500 font-mono">{idx + 1}</TableCell>
                    <TableCell>
                      <Select value={a.task_id} onValueChange={(v) => updateTask(idx, 'task_id', v)}>
                        <SelectTrigger className="w-full" data-testid={`task-select-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {masterTasks.filter(mt => mt.id === a.task_id || !assignments.some(x => x.task_id === mt.id)).map(mt => (
                            <SelectItem key={mt.id} value={mt.id}>{mt.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number" min={0} max={100} step={0.1}
                        value={a.weightage} onChange={(e) => updateTask(idx, 'weightage', parseFloat(e.target.value) || 0)}
                        className="w-24 text-center font-mono" data-testid={`weightage-${idx}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => removeTask(idx)} className="text-red-500 hover:text-red-700" data-testid={`remove-task-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {assignments.length > 0 && (
              <div className="p-4 border-t flex items-center justify-between">
                {Math.abs(totalWeightage - 100) > 0.01 && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> Weightage must total 100% (currently {totalWeightage}%)
                  </p>
                )}
                <div className="ml-auto">
                  <Button onClick={handleSave} disabled={saving || Math.abs(totalWeightage - 100) > 0.01} className="bg-[#138808] hover:bg-[#107307]" data-testid="save-assignments-btn">
                    <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Assignments'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Work Analysis */}
      {selectedEmpId && (
        <Card className="bg-white border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#FF9933]" /> Work Analysis - {selectedEmp?.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="space-y-1">
                <label className="text-xs text-slate-500">From</label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-40" data-testid="analysis-start" />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-slate-500">To</label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-40" data-testid="analysis-end" />
              </div>
              <Button onClick={fetchAnalysis} className="bg-[#FF9933] hover:bg-[#E68A2E]" data-testid="fetch-analysis-btn">
                <BarChart3 className="w-4 h-4 mr-1" /> Analyze
              </Button>
            </div>

            {analysis && (
              <div className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Days Tracked</p>
                    <p className="text-xl font-bold">{analysis.total_days}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-xs text-green-600">Avg Completion</p>
                    <p className="text-xl font-bold text-green-700">{analysis.average_percentage}%</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600">100% Days</p>
                    <p className="text-xl font-bold text-blue-700">{analysis.full_completion_days}</p>
                  </div>
                  <div className="p-3 bg-[#FF9933]/10 rounded-lg">
                    <p className="text-xs text-[#FF9933]">Consistency</p>
                    <p className="text-xl font-bold text-[#FF9933]">
                      {analysis.total_days > 0 ? Math.round((analysis.full_completion_days / analysis.total_days) * 100) : 0}%
                    </p>
                  </div>
                </div>

                {/* Task Breakdown */}
                {analysis.task_breakdown?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">Task-wise Breakdown</h3>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-semibold">Task</TableHead>
                          <TableHead className="font-semibold">Weightage</TableHead>
                          <TableHead className="font-semibold">Assigned</TableHead>
                          <TableHead className="font-semibold">Completed</TableHead>
                          <TableHead className="font-semibold">Rate</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {analysis.task_breakdown.map((tb, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium">{tb.task_name}</TableCell>
                            <TableCell>{tb.total_weightage}%</TableCell>
                            <TableCell>{tb.assigned_count} days</TableCell>
                            <TableCell>{tb.completed_count} days</TableCell>
                            <TableCell>
                              <Badge className={tb.completion_rate >= 80 ? 'bg-green-100 text-green-700' : tb.completion_rate >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-600'}>
                                {tb.completion_rate}%
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Daily Records */}
                {analysis.daily_records?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">Daily Records</h3>
                    <div className="max-h-80 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="font-semibold">Date</TableHead>
                            <TableHead className="font-semibold">Completion</TableHead>
                            <TableHead className="font-semibold">Tasks Done</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {analysis.daily_records.map((dr) => (
                            <TableRow key={dr.date}>
                              <TableCell className="font-medium">{dr.date}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full rounded-full bg-[#138808] transition-all" style={{ width: `${Math.min(dr.total_percentage, 100)}%` }} />
                                  </div>
                                  <span className={`text-sm font-bold ${dr.total_percentage >= 100 ? 'text-[#138808]' : dr.total_percentage >= 50 ? 'text-yellow-600' : 'text-red-500'}`}>
                                    {dr.total_percentage}%
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="text-sm text-slate-600">
                                {dr.tasks?.filter(t => t.is_completed).length || 0}/{dr.tasks?.length || 0}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
